import type React from "react"
import { Link } from "react-router-dom"
import { getTranslations } from "../i18n"
import { useLocale } from "../locale-context"

interface TopBarCrumb {
	label: string
	to: string
}

const ChevronRightIcon: React.FC = () => (
	<svg
		viewBox="0 0 16 16"
		fill="none"
		stroke="currentColor"
		strokeWidth="1.5"
		strokeLinecap="round"
		strokeLinejoin="round"
		className="w-4 h-4 shrink-0 text-muted-foreground/60"
		aria-hidden="true"
	>
		<path d="M6 4l4 4-4 4" />
	</svg>
)

/**
 * Breadcrumb trail for the mobile-only top bar (ZPL-269, mobile-only since
 * ZPL-353 — desktop context lives in the in-page header and the rail).
 * The property crumb always links to the property overview and, inside a
 * project, a second crumb links to the project root. Every crumb is a link
 * (no inert current-page crumb); each truncates under a max-width so long
 * names ellipsize instead of breaking the bar's layout.
 */
export function TopBarBreadcrumbs({ crumbs }: { crumbs: TopBarCrumb[] }) {
	const { locale } = useLocale()
	const t = getTranslations(locale).properties.detail

	return (
		<nav aria-label={t.breadcrumbsNavLabel} className="min-w-0">
			<ol className="flex items-center min-w-0">
				{crumbs.map((crumb, index) => (
					<li key={crumb.to} className="flex items-center min-w-0">
						{index > 0 ? <ChevronRightIcon /> : null}
						<Link
							to={crumb.to}
							className={`min-w-0 max-w-[40vw] truncate px-1.5 py-1 rounded text-sm transition-colors ${
								index === crumbs.length - 1
									? "font-bold text-foreground"
									: "font-medium text-muted-foreground hover:text-foreground"
							}`}
						>
							{crumb.label}
						</Link>
					</li>
				))}
			</ol>
		</nav>
	)
}
